import { createContext, Dispatch, RefObject, SetStateAction, use } from 'react';

import { AddLayoutRequest, LayoutUpdateRequest, SiteControlLayout } from '@scaleaq/sitecontrol-lib';

import { useRequiredContext } from '@/hooks';
import type { AutoSaveWidgetConfig } from '@/pages/landingPage/landingPageProvider/useAutoSaveWidgetConfig.ts';
import type { GridAreaSpec } from '@/utils/gridSpec';

export type GridEventMap = {
  onRemove: { widgetId: string };
  onSwitchWidgetContent: { widgetId: string };
};

export type GridEventBus = {
  registerEvent<K extends keyof GridEventMap>(
    event: K,
    handler: (payload: GridEventMap[K]) => void
  ): () => void;
  fireEvent<K extends keyof GridEventMap>(event: K, payload: GridEventMap[K]): void;
};

export interface LandingPageContextProps {
  addLayout: (addLayoutRequest: AddLayoutRequest) => Promise<SiteControlLayout>;
  addWidget: () => void;
  /** Re-tiles the current layout's widgets into the cells of a grid spec that fills the screen. */
  applyGrid: (spec: GridAreaSpec) => void;
  checkIsLayoutInDb: (id: string) => boolean;
  compact: boolean;
  currentLayout: SiteControlLayout | undefined;
  currentLayoutInDb: SiteControlLayout | undefined;
  defaultAddLayoutRequest: AddLayoutRequest | undefined;
  defaultNewLayout: SiteControlLayout;
  existingLayoutIds: string[];
  existingLayoutNames: string[];
  gridEventBus: GridEventBus;
  handleReplaceWidget: (widgetId: string) => void;
  isDirty: boolean;
  isEditMode: boolean;
  setIsEditMode: Dispatch<SetStateAction<boolean>>;
  isOnLandingPage: boolean;
  resetLayout: () => void;
  /** Ref set by Grid so resetLayout can trigger re-apply of layout to grid after reset to DB. */
  applyLayoutToGridRef: RefObject<(() => void) | null>;
  saveNewLayout: (layoutUpdateRequest: LayoutUpdateRequest) => Promise<SiteControlLayout>;
  saveLayout: () => void;
  setCompact: Dispatch<SetStateAction<boolean>>;
  /** Writes a widget-config change and persists it right away, keeping the layout out of edit mode. */
  updateWidgetConfigAndSave: AutoSaveWidgetConfig['updateWidgetConfigAndSave'];
}

export const LandingPageContext = createContext<LandingPageContextProps | undefined>(undefined);
LandingPageContext.displayName = 'LandingPageContext';

export const useLandingPageContext = (): LandingPageContextProps =>
  useRequiredContext({
    context: LandingPageContext,
    hookName: 'useLandingPageContext',
    providerName: 'a LandingPageProvider',
  });

/** Returns context or undefined when outside LandingPageProvider. Use in shared components that render on both (dashboard) and (app) routes. */
export const useOptionalLandingPageContext = () => use(LandingPageContext);
